import { useRef, useState, useEffect } from 'react';
import { useIsomorphicLayoutEffect } from '@shopify/react-hooks';
import { ResizeObserver } from '@juggle/resize-observer';
import { isHTMLElement } from '../../utilities/dom.mjs';

const ResizeObserverPolyfilled = typeof window !== 'undefined' && window.ResizeObserver || ResizeObserver;
function useResizeObserver(elementOrRef, onResize, options) {
  const elementRef = useRef(null);
  const resizeObserverCallback = useRef(onResize);
  const [resizeObserver] = useState(() => {
    return new ResizeObserverPolyfilled(([entry]) => {
      resizeObserverCallback.current(elementRef.current, entry);
    });
  });
  useIsomorphicLayoutEffect(() => {
    const element = getElement(elementOrRef);
    if (element !== elementRef.current) {
      if (elementRef.current) {
        resizeObserver.disconnect();
      }
      if (element) {
        resizeObserver.observe(element);
        if (options != null && options.observeChildren) {
          for (const child of element.children) {
            resizeObserver.observe(child);
          }
        }
      }
      resizeObserverCallback.current(element != null ? element : null);
      elementRef.current = element != null ? element : null;
    }
  });
  useIsomorphicLayoutEffect(() => {
    resizeObserverCallback.current = onResize;
  }, [onResize]);

  // Disconnect resize observer on unmount
  useEffect(() => {
    return () => resizeObserver.disconnect();
  }, [resizeObserver]);
}
function getElement(element) {
  if (!element) {
    return null;
  }
  if ('current' in element) {
    return element.current;
  }
  if (isHTMLElement(element)) {
    return element;
  }
  return null;
}

export { useResizeObserver };
